import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { AlertDialog, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, Input, SpecraButton, SpecraDataTable, SpecraFormField, specraButtonVariants } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useState } from 'react';
import { usePurgeProgram, usePurgeProgramPreview } from './hooks';
// "delivery.program_tasks" → "program tasks"; the schema is noise to a reader.
const tableLabel = (t)=>t.slice(t.indexOf('.') + 1).replace(/_/g, ' ');
// Deletes and the SET NULL / revoke / re-parent updates, in one cell.
const effect = (t)=>[
        Number(t.rowsDeleted) > 0 && `${t.rowsDeleted} deleted`,
        Number(t.rowsUpdated) > 0 && `${t.rowsUpdated} unlinked or revoked`
    ].filter(Boolean).join(' · ');
/**
 * Admin-only, irreversible. Unlike Archive (a soft delete the data survives),
 * a purge destroys the program and everything attached to it. The dialog
 * shows the server's dry-run of exactly what will go, and arms only once the
 * program's full name is typed — the server checks the same name again.
 */ export function PurgeProgramDialog({ open, onOpenChange, programId, programName, onPurged }) {
    const [typed, setTyped] = useState('');
    const preview = usePurgeProgramPreview(programId, open);
    const purge = usePurgeProgram();
    const tables = preview.data?.tables ?? [];
    const destroyed = tables.reduce((n, t)=>n + Number(t.rowsDeleted), 0);
    const armed = typed.trim() === programName && !!preview.data && !purge.isPending;
    const close = (next)=>{
        if (!next) setTyped('');
        onOpenChange(next);
    };
    return /*#__PURE__*/ _jsx(AlertDialog, {
        open: open,
        onOpenChange: close,
        children: /*#__PURE__*/ _jsxs(AlertDialogContent, {
            "data-testid": Testids.deliveryPrograms.purgeDialog,
            children: [
                /*#__PURE__*/ _jsxs(AlertDialogHeader, {
                    children: [
                        /*#__PURE__*/ _jsxs(AlertDialogTitle, {
                            children: [
                                "Permanently purge “",
                                programName,
                                "”?"
                            ]
                        }),
                        /*#__PURE__*/ _jsx(AlertDialogDescription, {
                            asChild: true,
                            children: /*#__PURE__*/ _jsxs("div", {
                                className: "space-y-2 text-sm",
                                children: [
                                    /*#__PURE__*/ _jsxs("p", {
                                        children: [
                                            /*#__PURE__*/ _jsx("strong", {
                                                className: "text-destructive",
                                                children: "This cannot be undone."
                                            }),
                                            " Purging is not archiving: the program and every record below are deleted from the database for good, its access grants are revoked, and its assistant memories are erased."
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx("p", {
                                        className: "text-xs text-muted-foreground",
                                        children: "Uploaded files stay in their project, and the rate book and pre-award records it was linked to are kept and unlinked."
                                    })
                                ]
                            })
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: "max-h-56 overflow-y-auto",
                    children: preview.error ? /*#__PURE__*/ _jsxs("p", {
                        className: "text-xs text-destructive",
                        children: [
                            "Could not preview the purge: ",
                            preview.error.message
                        ]
                    }) : /*#__PURE__*/ _jsx(SpecraDataTable, {
                        caption: "What the purge destroys",
                        columns: [
                            {
                                id: 'table',
                                header: `${destroyed} records destroyed`,
                                cell: (t)=>tableLabel(t.table)
                            },
                            {
                                id: 'effect',
                                header: 'effect',
                                align: 'right',
                                cell: effect
                            }
                        ],
                        getRowId: (t)=>t.table,
                        getRowProps: (t)=>({
                                'data-table': t.table
                            }),
                        isLoading: preview.isLoading,
                        loadingLabel: "Counting what would be destroyed…",
                        rows: tables,
                        rowTestId: Testids.deliveryPrograms.purgeImpactRow,
                        // The data-table min width is sized for pages; this dialog is narrower.
                        tableProps: {
                            className: 'min-w-0'
                        }
                    })
                }),
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            "Type ",
                            /*#__PURE__*/ _jsx("strong", {
                                className: "select-all",
                                children: programName
                            }),
                            " to confirm"
                        ]
                    }),
                    children: /*#__PURE__*/ _jsx(Input, {
                        autoComplete: "off",
                        "data-testid": Testids.deliveryPrograms.purgeConfirmInput,
                        onChange: (e)=>setTyped(e.target.value),
                        value: typed
                    })
                }),
                /*#__PURE__*/ _jsxs(AlertDialogFooter, {
                    children: [
                        /*#__PURE__*/ _jsx(AlertDialogCancel, {
                            className: specraButtonVariants({
                                variant: 'secondary'
                            }),
                            disabled: purge.isPending,
                            children: "Cancel"
                        }),
                        /*#__PURE__*/ _jsx(SpecraButton, {
                            "data-testid": Testids.deliveryPrograms.purgeConfirmButton,
                            disabled: !armed,
                            // mutate, not mutateAsync: a refusal is toasted by the hook's
                            // onError and leaves the dialog open, with no unhandled rejection.
                            onClick: ()=>purge.mutate({
                                    id: programId,
                                    confirmName: typed.trim()
                                }, {
                                    onSuccess: ()=>{
                                        close(false);
                                        onPurged();
                                    }
                                }),
                            variant: "danger",
                            children: purge.isPending ? 'Purging…' : 'Purge permanently'
                        })
                    ]
                })
            ]
        })
    });
}
